import { useEffect, useRef, useState } from 'react'
import { Toaster } from 'sonner'
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import Calendar from './pages/Calendar'
import Home from './pages/Home'
import NotFound from './pages/NotFound'
import Login from './pages/Auth/Login'
import Register from './pages/Auth/Register'
import ForgotPassword from './pages/Auth/ForgotPassword'
import UpdatePassword from './pages/Auth/UpdatePassword'
import { FamilySettings } from './pages/FamilySettings'
import { AcceptInvitation } from './pages/AcceptInvitation'
import PrivacyPolicy from './pages/PrivacyPolicy'
import Terms from './pages/Terms'
import Account from './pages/Account'
import Pricing from './pages/Pricing'
import BlogIndex from './pages/BlogIndex'
import BlogPostPage from './pages/BlogPostPage'
import Contact from './pages/Contact'
import { useAuthStore } from './stores/authStore'
import { useFamilyStore } from './stores/familyStore'
import { useUserPreferencesStore } from './stores/userPreferencesStore'
import logger from './utils/logger'
import { track, readPurchaseReturn } from './utils/analytics'

// Komponent ładowania
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
      <p className="text-slate-600">Ładowanie...</p>
    </div>
  </div>
)

// Komponent dla chronionych tras (wymaga zalogowania)
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, initialized } = useAuthStore()
  const { initialized: familyInitialized, isLoading: familyLoading, initialize: initializeFamily } = useFamilyStore()
  const navigate = useNavigate()
  const [localInitializing, setLocalInitializing] = useState(false)

  // Użyj useRef aby zapobiec wielokrotnej inicjalizacji
  const hasStartedInitRef = useRef(false)

  // Inicjalizuj familyStore gdy użytkownik jest zalogowany
  useEffect(() => {
    if (initialized && !loading && user && !familyInitialized && !hasStartedInitRef.current) {
      logger.debug('🔄 ProtectedRoute: Inicjalizacja familyStore dla użytkownika:', user.email)
      hasStartedInitRef.current = true
      setLocalInitializing(true)

      // Wywołaj inicjalizację i czekaj na wynik
      initializeFamily()
        .catch(error => {
          logger.error('❌ Błąd inicjalizacji familyStore:', error)
        })
        .finally(() => {
          setLocalInitializing(false)
        })
    }
  }, [initialized, loading, user, familyInitialized]) // Usuń familyLoading z zależności

  // Reset flagi gdy użytkownik się zmieni
  useEffect(() => {
    if (!user) {
      hasStartedInitRef.current = false
    }
  }, [user])

  useEffect(() => {
    if (initialized && !loading && !user) {
      navigate('/auth/login')
    }
  }, [user, loading, initialized, navigate])

  if (!initialized || loading) {
    return <LoadingScreen />
  }

  if (!user) {
    return <Navigate to="/auth/login" />
  }

  // Czekaj na inicjalizację familyStore dla zalogowanych użytkowników
  // Sprawdzaj zarówno stan z store'a jak i lokalną flagę
  if (familyLoading || localInitializing) {
    logger.debug('⏳ ProtectedRoute: Czekam na inicjalizację familyStore', {
      familyInitialized,
      familyLoading,
      localInitializing
    })
    return <LoadingScreen />
  }

  return <>{children}</>
}

// Komponent dla strony głównej - przekierowuje zalogowanych do kalendarza
const HomeRoute = () => {
  const { user, loading, initialized } = useAuthStore()
  const { initialized: familyInitialized, isLoading: familyLoading, initialize: initializeFamily } = useFamilyStore()
  const [localInitializing, setLocalInitializing] = useState(false)

  // Użyj useRef aby zapobiec wielokrotnej inicjalizacji
  const hasStartedInitRef = useRef(false)

  // Inicjalizuj familyStore gdy użytkownik jest zalogowany
  useEffect(() => {
    if (initialized && !loading && user && !familyInitialized && !hasStartedInitRef.current) {
      logger.debug('🔄 HomeRoute: Inicjalizacja familyStore dla użytkownika:', user.email)
      hasStartedInitRef.current = true
      setLocalInitializing(true)

      // Wywołaj inicjalizację i czekaj na wynik
      initializeFamily()
        .catch(error => {
          logger.error('❌ Błąd inicjalizacji familyStore:', error)
        })
        .finally(() => {
          setLocalInitializing(false)
        })
    }
  }, [initialized, loading, user, familyInitialized]) // Usuń familyLoading z zależności

  // Reset flagi gdy użytkownik się zmieni
  useEffect(() => {
    if (!user) {
      hasStartedInitRef.current = false
    }
  }, [user])

  if (!initialized || loading) {
    return <LoadingScreen />
  }

  // Jeśli użytkownik jest zalogowany → zawsze prowadź do kalendarza
  if (user) {
    if (familyLoading || localInitializing) {
      return <LoadingScreen />
    }
    return <Navigate to="/calendar" replace />
  }

  // Jeśli nie jest zalogowany, pokaż stronę główną
  return <Home />
}

// Komponent dla tras publicznych (dostępne dla niezalogowanych)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, initialized } = useAuthStore()

  if (!initialized || loading) {
    return <LoadingScreen />
  }

  return !user ? <>{children}</> : <Navigate to="/calendar" />
}

// (Usunięto OnboardingRoute – nieużywany)

import CookieConsent from './components/CookieConsent'

// ... existing imports

export default function App() {
  const { initialize, initialized, loading, user } = useAuthStore()
  const { loadPreferences } = useUserPreferencesStore()

  // Użyj useRef aby zapobiec wielokrotnej inicjalizacji
  const hasInitializedRef = useRef(false)

  useEffect(() => {
    logger.debug('🚀 App useEffect - initialized:', initialized, 'loading:', loading)

    // Inicjalizuj auth store przy starcie aplikacji - tylko raz!
    if (!initialized && !hasInitializedRef.current) {
      logger.debug('🔄 Inicjalizacja auth store...')
      hasInitializedRef.current = true
      initialize()
    }
  }, [initialize, initialized])

  // Załaduj preferencje użytkownika gdy zmieni się status auth lub użytkownik
  useEffect(() => {
    if (initialized && !loading && user) {
      loadPreferences()
    }
  }, [initialized, loading, user, loadPreferences])

  // Dodaj timeout dla inicjalizacji
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!initialized) {
        logger.warn('⏰ Timeout inicjalizacji auth store - wymuszam zakończenie')
        // Wymuszamy zakończenie inicjalizacji po 15 sekundach
        // To zapewni, że użytkownik zobaczy ekran logowania
      }
    }, 15000)

    return () => clearTimeout(timeout)
  }, [initialized])

  if (!initialized || loading) {
    logger.debug('⏳ App loading - initialized:', initialized, 'loading:', loading)
    return <LoadingScreen />
  }

  return (
    <Router>
      <Toaster position="bottom-center" />
      <CookieConsent />
      {/* Ręczne page_view przy zmianie ścieżki z maskowaniem /invitation/* */}
      <RouteChangeTracker />
      <Routes>
        {/* Strona główna */}
        <Route path="/" element={<HomeRoute />} />

        {/* Strony autoryzacji */}
        <Route path="/auth/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/auth/register" element={<PublicRoute><Register /></PublicRoute>} />
        <Route path="/auth/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
        <Route path="/auth/update-password" element={<UpdatePassword />} />

        {/* Onboarding (zachowany dla kompatybilności – przekierowanie do ustawień) */}
        <Route path="/onboarding" element={<ProtectedRoute><FamilySettings /></ProtectedRoute>} />

        {/* Kalendarz - wymaga zalogowania */}
        <Route path="/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />

        {/* Zarządzanie rodziną */}
        <Route path="/family-settings" element={<ProtectedRoute><FamilySettings /></ProtectedRoute>} />
        {/* Konto */}
        <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />

        {/* Zaproszenia */}
        <Route path="/invitation/:token" element={<AcceptInvitation />} />

        {/* Polityka prywatności – dostępna publicznie i dla zalogowanych */}
        <Route path="/polityka-prywatnosci" element={<PrivacyPolicy />} />
        {/* Stara ścieżka dla kompatybilności */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />

        {/* Regulamin – dostępny publicznie i dla zalogowanych */}
        <Route path="/terms" element={<Terms />} />

        {/* Cennik – dostępny publicznie i dla zalogowanych */}
        <Route path="/pricing" element={<Pricing />} />

        {/* Blog – publiczny */}
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />

        {/* Kontakt – dostępny publicznie */}
        <Route path="/contact" element={<Contact />} />

        {/* Strona 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  )
}

function RouteChangeTracker() {
  const location = useLocation()
  const navigate = useNavigate()
  const prevPathRef = useRef<string | null>(null)

  // Initial page_view (jeśli nie wysłany wcześniej przez index.html)
  useEffect(() => {
    try {
      const w = window as any
      if (!w.__pvSent) {
        const initialPath = window.location.pathname
        const maskedInitial = initialPath.startsWith('/invitation/') ? '/invitation/[token]' : initialPath
        track('page_view', { page_path: maskedInitial })
        w.__pvSent = true
        prevPathRef.current = initialPath
      }
    } catch { }
  }, [])

  // Page_view przy zmianie ścieżki
  useEffect(() => {
    const path = location.pathname || window.location.pathname
    if (prevPathRef.current === path) return
    prevPathRef.current = path
    const masked = path.startsWith('/invitation/') ? '/invitation/[token]' : path
    track('page_view', { page_path: masked })
  }, [location.pathname])

  // Zakup: powrót ze Stripe Checkout (successUrl z Pricing.tsx); parametry usuwamy z adresu, by odświeżenie nie liczyło zakupu ponownie
  useEffect(() => {
    const ret = readPurchaseReturn(location.search)
    if (!ret) return
    track('purchase', ret.params)
    navigate({ pathname: location.pathname, search: ret.cleanedSearch, hash: location.hash }, { replace: true })
  }, [location.search, location.pathname, location.hash, navigate])

  return null
}